import { defineStore, acceptHMRUpdate } from 'pinia'
import * as taskAPI from '@/scripts/task'
import { Loader } from '@googlemaps/js-api-loader'
import { toRaw } from 'vue'
import * as d3 from 'd3'
import { colors } from '../scripts/util/colors'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import { useTasksStore } from './tasks'
import { transformBordersToGeoJSON } from '../scripts/util/maps'
import * as turf from '@turf/turf'
import { useCustomers } from './customers'
import '@/styles/mapControls.scss'
import { createVNode, render } from 'vue'
import FieldLayersControl from '@/views/field/FieldLayersControl.vue'
import MapTypeControl from '@/components/map/MapTypeControl.vue'
import app from '@/main'
import { useMeasure } from './measuring'
import { rainbowColors } from '../helpers/rainbowColors'
import moment from 'moment'
import { saveAs } from 'file-saver'
import { getInitialsCode } from '@/helpers/stringHelper'
import { useFieldsConnectionStore } from './fieldsConnection'
import { useAppState } from './appState'
import FindPlaces from '@/components/map/FindPlaces.vue'
import { useFieldsManagementStore } from './fieldsManagement'
import { TerraDraw } from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'
import {
    TerraDrawPointMode,
    TerraDrawLineStringMode,
    TerraDrawPolygonMode,
    TerraDrawRectangleMode,
    TerraDrawAngledRectangleMode,
    TerraDrawCircleMode,
    TerraDrawFreehandMode,
    TerraDrawSelectMode
} from 'terra-draw'
import { useWarningPlanner } from './warningPlanner'
import { useFieldLayersStore } from './fieldLayers'
import { downloadFeatureAsShapefile } from '@/scripts/shapefileExport'
import YieldMapLegend from '@/components/map/YieldMapLegend.vue'
import IsoPreviewLegend from '@/components/map/IsoPreviewLegend.vue'
import AsAppliedMapLegend from '@/components/map/AsAppliedMapLegend.vue'
import VehicleGpsRouteLegend from '@/components/map/VehicleGpsRouteLegend.vue'
import VehicleLoadingLiveMapLegend from '@/components/map/VehicleLoadingLiveMapLegend.vue'
import i18n from '@/i18n'

// Up to this zoom the clusterer takes over and field names stay hidden.
const FIELD_LABEL_MIN_ZOOM = 13

export const useMaps = defineStore('maps', {
    state: () => ({
        filteredFields: [],
        disabledFields: [],
        searchInput: '',
        selected: null,
        loading: {
            fieldList: true,
            map: true,
            fieldDetails: false, // used for field details skeleton
            fieldTasks: false, // used for field tasks skeleton
            fieldDisable: false, // used while disabling a field
            fieldRestore: false, // used while restoring a field
        },
        geo: {
            lat: 0,
            long: 0,
            zoom: 2
        },
        listeners: {
            zoom: null,
            dragStart: null,
            dragEnd: null,
        },
        terraDraw: null,
        terraDrawAdapter: null,
        draw: {
            fields: [],
            field: {
                id: null,
                abLines: [],
                abLinesMarkers: [],
                headlanes: [],
                fieldHeadlanes: [], // ONLY field headlanes, not for obstacles headlanes (used for selecting headland target)
                headlanesMarkers: [],
                innerBorder: [],
                segments: [],
                hasSpecialABLines: false,
                trackWheels: [],
            },
        },
        map: null,
        mapInDOM: null,
        dragging: false,
        mapCenter: null,
        google: null,
        markerCluster: null,
        markerSpidifier: null,
        fields: [],
        tasks: {},
        plans: [],
        fieldsLoaded: 0,
        fieldsTotal: 0,
        fieldsErrored: 0,
        mapControls: {
            layersControl: null
        },
        controlContainer: null,
        controlLayersDiv: null,
        rainbowColors: rainbowColors,
        lastMapType: 'satellite',
        findPlacesControl: null,
        randomRainbowColors: [...rainbowColors], // save a copy to prevent mutation
        pulseHeadlandInterval: null,
        controlYieldLegendDiv: null,
        controlIsoPreviewLegendDiv: null,
        controlVehicleGpsLegendDiv: null,
    }),

    getters: {},
    actions: {
        buildPath(view, customerId, coords = true) {
            const { lat, long, zoom } = this.geo
            if (customerId != null) {
                if (coords) {
                    return `/${view}/${customerId}/${lat}/${long}/${zoom}`
                }   
                return `/${view}/${customerId}`
            }
            if (coords) {
                return `/${view}/${lat}/${long}/${zoom}`
            }
            return `/${view}`
        },
        //#region Google Maps
        async initMap() {
            let map = null
            let key = import.meta.env.VITE_APP_MAP_KEY
            const loader = new Loader({
                apiKey: key,
                version: 'weekly',
                libraries: [],
            })

            await loader.importLibrary('maps').then(({ Map }) => {

                /**
                 * Custom Function for drawing fields
                 */
                window.google.maps.Polygon.prototype.poly_getBounds = function () {
                    var bounds = new window.google.maps.LatLngBounds()
                    this.getPath().forEach(function (element) {
                        bounds.extend(element)
                    })
                    return bounds
                }
                /**
                 * Get the google context into the store
                 */
                this.google = window.google

                /**
                 * Map default options
                 */
                let mapOptions = {
                    center: { lat: 0, lng: 0 },
                    zoom: 2,
                    mapTypeControl: false,
                    streetViewControl: false,
                    cameraControl: false,
                    mapId: 'nst-field-manager',
                    mapTypeId: this.lastMapType,
                }
                /**
                 * Create the map
                 */
                map = new Map(document.getElementById('map'), mapOptions)
                /**
                 * Get the map context into the store
                 */
                this.map = map
                this.mapInDOM = document.getElementById('map')
                this.addListeners()

                // Add the Field Layers Control
                this.addFieldLayersControl()

                // Add the Find Places Control
                this.addFindPlacesControl()

                // Add Yield Map legend
                this.addYieldMapLegend()

                // Add ISO Preview legend
                this.addIsoPreviewLegend()

                // Add as-applied map legend
                this.addAsAppliedMapLegend()

                // Add vehicle GPS route legend
                this.addVehicleGpsRouteLegend()

                // Add loading legend for live vehicle positions on the fields map
                this.addVehicleLoadingLiveMapLegend()
            })

            /**
             * Load the places library
             */
            await loader.importLibrary('places')

            /**
             * Load the marker library
             */
            await loader.importLibrary('marker')

            /**
             * Initialize TerraDraw for drawing functionality
             */
            // Create TerraDraw adapter
            this.terraDrawAdapter = new TerraDrawGoogleMapsAdapter({
                map: this.map,
                lib: window.google.maps,
                coordinatePrecision: 15,
                // Keep TerraDraw off the shared map.data layer so planner/measuring
                // setStyle() calls cannot override TerraDraw polygon colors/markers.
                isolatedData: true
            })

            // Create TerraDraw instance with drawing modes
            this.terraDraw = new TerraDraw({
                adapter: this.terraDrawAdapter,
                modes: [
                    new TerraDrawPointMode(),
                    new TerraDrawLineStringMode({
                        pointerDistance: 5,
                        styles: {
                            lineStringColor: '#ff0000',
                            lineStringWidth: 2,
                            closingPointColor: '#ff0000',
                            closingPointWidth: 4,
                            closingPointOutlineColor: '#ffffff',
                            closingPointOutlineWidth: 1,
                        }
                    }),
                    new TerraDrawPolygonMode({
                        pointerDistance: 5,
                        styles: {
                            outlineColor: '#ff0000',
                            fillOpacity: 0.1,
                            outlineWidth: 2,
                            closingPointWidth: 4,
                            closingPointOutlineWidth: 1,
                        }
                    }),
                    new TerraDrawRectangleMode({
                        styles: {
                            outlineColor: '#ff0000',
                            fillOpacity: 0.1,
                            outlineWidth: 2,
                        },
                    }),
                    new TerraDrawAngledRectangleMode({
                        styles: {
                            fillColor: '#9AE630',
                            outlineColor: '#5EA529',
                            outlineWidth: 3,
                            fillOpacity: 0.5,
                        },
                    }),
                    new TerraDrawCircleMode({
                        pointerDistance: 5,
                        styles: {
                            outlineColor: '#ff0000',
                            fillOpacity: 0.1,
                            outlineWidth: 2,
                        },
                    }),
                    new TerraDrawFreehandMode({
                        pointerDistance: 1,
                        styles: {
                            outlineColor: '#ff0000',
                            fillOpacity: 0.1,
                            outlineWidth: 2,
                            closingPointWidth: 4,
                            closingPointOutlineWidth: 1,
                        }
                    }),
                    new TerraDrawSelectMode({
                        flags: {
                            rectangle: {
                                feature: {
                                    draggable: true,
                                    rotateable: false,
                                    coordinates: {
                                        draggable: false,
                                        resizable: false,
                                        midpoints: false,
                                        deletable: false,
                                    },
                                },
                            },
                            'angled-rectangle': {
                                feature: {
                                    draggable: true,
                                    rotateable: false,
                                    coordinates: {
                                        draggable: false,
                                        resizable: false,
                                        midpoints: false,
                                        deletable: false,
                                    },
                                },
                            },
                            polygon: {
                                feature: {
                                    draggable: true,
                                    rotateable: false,
                                    coordinates: {
                                        draggable: false,
                                        resizable: false,
                                        midpoints: false,
                                        deletable: false,
                                    },
                                },
                            },
                        },
                    })
                ]
            })
            /**
             * Initialize the map center
             */
            this.mapCenter = new this.google.maps.LatLngBounds()

            // Add the container for controls
            this.controlContainer = document.createElement("div")
            this.controlContainer.className = "control-container"

            // Add the map type control to the map
            this.addMapTypeControl()

            // Add the map distance control
            const measureStore = useMeasure()
            measureStore.addMapDistanceControl()

            // Add the controls to the map
            map.controls[this.google.maps.ControlPosition.RIGHT_BOTTOM].push(this.controlContainer)

            this.changeMapType()

            this.loading.map = false
        },
        changeMapType() {
            const maxZoomService = new this.google.maps.MaxZoomService()

            this.map.addListener('zoom_changed', () => {
                const mapType = this.map.getMapTypeId()
                const zoom = this.map.getZoom()

                maxZoomService.getMaxZoomAtLatLng(this.map.getCenter(), (response) => {
                    if (response.status !== 'OK') return

                    const maxZoom = response.zoom

                    // Zoomed in to max → switch to roadmap
                    if (zoom >= maxZoom) {
                        this.map.setMapTypeId('roadmap')
                    }
                    // Zoomed out from max → switch back to satellite
                    else if (mapType === 'roadmap' && zoom <= maxZoom) {
                        this.map.setMapTypeId(this.lastMapType)
                    }
                })
            })
        },
        addMapTypeControl() {
            // Create a container for the Vue component
            const controlDiv = document.createElement('div')

            // Create a Vue VNode, check context and render it inside the div
            const vnode = createVNode(MapTypeControl)
            vnode.appContext = app._context //Making all context visible
            render(vnode, controlDiv)

            this.controlContainer.appendChild(controlDiv)
        },
        addFindPlacesControl() {
            // Create a container for the Vue component
            this.findPlacesControl = document.createElement('div')
            this.findPlacesControl.className = 'find-places-map-control'
            // this.findPlacesControl.style.margin = '0 10px'
            this.findPlacesControl.style.position = 'absolute'
            this.findPlacesControl.style.right = '50px' // Face loc pentru butonul full map (4px + 32px + 14px)
            this.findPlacesControl.style.top = '10px'
            this.findPlacesControl.style.zIndex = '1000'

            // Create a Vue VNode, check context and render it inside the div
            const vnode = createVNode(FindPlaces)
            vnode.appContext = app._context //Making all context visible
            render(vnode, this.findPlacesControl)

            // Add the control directly to the map container instead of controls
            this.mapInDOM.appendChild(this.findPlacesControl)
        },

        // Add this new method to create the FieldLayersControl on the map
        addFieldLayersControl() {
            // Create a container for the Vue component
            this.controlLayersDiv = document.createElement('div')
            this.controlLayersDiv.className = 'field-layers-map-control'
            this.controlLayersDiv.style.margin = '0 10px'

            // Create a Vue VNode, check context and render it inside the div
            const vnode = createVNode(FieldLayersControl)
            vnode.appContext = app._context //Making all context visible
            render(vnode, this.controlLayersDiv)

            // Add the control to the map at the TOP_LEFT position
            this.map.controls[this.google.maps.ControlPosition.TOP_LEFT].push(this.controlLayersDiv)
        },
        addYieldMapLegend() {
            // Create a container for the Vue component
            this.controlYieldLegendDiv = document.createElement('div')
            this.controlYieldLegendDiv.className = 'yield-map-legend-control'
            this.controlYieldLegendDiv.style.margin = '4px 0px '
            this.controlYieldLegendDiv.style.position = 'absolute'
            this.controlYieldLegendDiv.style.zIndex = '1000'

            // Create a Vue VNode, check context and render it inside the div
            const vnode = createVNode(YieldMapLegend)
            vnode.appContext = app._context // Making all context visible
            render(vnode, this.controlYieldLegendDiv)

            // Add the control to the map at the BOTTOM_LEFT position
            this.map.controls[this.google.maps.ControlPosition.BOTTOM_LEFT].push(this.controlYieldLegendDiv)
        },
        addIsoPreviewLegend() {
            this.controlIsoPreviewLegendDiv = document.createElement('div')
            this.controlIsoPreviewLegendDiv.className = 'iso-preview-legend-control'
            this.controlIsoPreviewLegendDiv.style.margin = '4px 0px'
            this.controlIsoPreviewLegendDiv.style.position = 'absolute'
            this.controlIsoPreviewLegendDiv.style.zIndex = '1000'

            const vnode = createVNode(IsoPreviewLegend)
            vnode.appContext = app._context
            render(vnode, this.controlIsoPreviewLegendDiv)

            this.map.controls[this.google.maps.ControlPosition.BOTTOM_LEFT].push(this.controlIsoPreviewLegendDiv)
        },
        addAsAppliedMapLegend() {
            this.controlAsAppliedLegendDiv = document.createElement('div')
            this.controlAsAppliedLegendDiv.className = 'as-applied-map-legend-control'
            this.controlAsAppliedLegendDiv.style.margin = '4px 0px'
            this.controlAsAppliedLegendDiv.style.position = 'absolute'
            this.controlAsAppliedLegendDiv.style.zIndex = '1000'

            const vnode = createVNode(AsAppliedMapLegend)
            vnode.appContext = app._context
            render(vnode, this.controlAsAppliedLegendDiv)

            this.map.controls[this.google.maps.ControlPosition.BOTTOM_LEFT].push(this.controlAsAppliedLegendDiv)
        },
        addVehicleGpsRouteLegend() {
            this.controlVehicleGpsLegendDiv = document.createElement('div')
            this.controlVehicleGpsLegendDiv.className = 'vehicle-gps-legend-control'
            this.controlVehicleGpsLegendDiv.style.margin = '4px 0px'
            this.controlVehicleGpsLegendDiv.style.position = 'absolute'
            this.controlVehicleGpsLegendDiv.style.zIndex = '1000'

            const vnode = createVNode(VehicleGpsRouteLegend)
            vnode.appContext = app._context
            render(vnode, this.controlVehicleGpsLegendDiv)

            this.map.controls[this.google.maps.ControlPosition.BOTTOM_LEFT].push(this.controlVehicleGpsLegendDiv)
        },
        addVehicleLoadingLiveMapLegend() {
            this.controlVehicleLoadingLiveMapLegendDiv = document.createElement('div')
            this.controlVehicleLoadingLiveMapLegendDiv.className = 'vehicle-loading-live-map-legend-control'
            this.controlVehicleLoadingLiveMapLegendDiv.style.margin = '4px 0px'
            this.controlVehicleLoadingLiveMapLegendDiv.style.position = 'absolute'
            this.controlVehicleLoadingLiveMapLegendDiv.style.zIndex = '1000'

            const vnode = createVNode(VehicleLoadingLiveMapLegend)
            vnode.appContext = app._context
            render(vnode, this.controlVehicleLoadingLiveMapLegendDiv)

            this.map.controls[this.google.maps.ControlPosition.BOTTOM_LEFT].push(this.controlVehicleLoadingLiveMapLegendDiv)
        },
        addListeners() {
            this.listeners.zoom = this.google.maps.event.addListener(this.map, "zoom_changed", () => {
                this.geo.zoom = this.map.getZoom()
                this.geo.lat = this.map.getCenter().lat()
                this.geo.long = this.map.getCenter().lng()
                // this.router.push(`/fields/${this.geo.lat}/${this.geo.long}/${this.geo.zoom}`)
                this.updateFieldLabelVisibility()
            })
            this.listeners.dragStart = this.google.maps.event.addListener(this.map, "dragstart", () => {
                this.dragging = true
            })
            this.listeners.dragEnd = this.google.maps.event.addListener(this.map, "dragend", () => {
                // this.router.push(`/fields/${this.geo.lat}/${this.geo.long}/${this.geo.zoom}`)
                this.dragging = false
                this.geo.lat = this.map.getCenter().lat()
                this.geo.long = this.map.getCenter().lng()
                this.geo.zoom = this.map.getZoom()
            })
            this.updateFieldLabelVisibility()
        },
        updateFieldLabelVisibility() {
            const hide = this.map.getZoom() <= FIELD_LABEL_MIN_ZOOM
            this.mapInDOM?.classList.toggle('hide-field-labels', hide)
        },
        extendMapCenter(coords) {
            if (!coords || !Array.isArray(coords)) return
            coords.map((c) => {
                this.mapCenter.extend({
                    lat: c[1],
                    lng: c[0],
                })
            })
        },
        genFieldBounds(coords) {
            let bounds = new this.google.maps.LatLngBounds()
            coords.map((c) => {
                bounds.extend({
                    lat: c[1],
                    lng: c[0],
                })
            })
            return bounds
        },

        //#endregion

        drawLabel(f) {
            /**We gotta load the class AFTER google injected into the app :/ */
            let coords = {
                lat: f.poly.poly_getBounds().getCenter().lat(),
                lng: f.poly.poly_getBounds().getCenter().lng(),
            }

            let content = document.createElement('span')
            content.textContent = f.field_name?.trim() || i18n.global.t('Untitled')
            content.classList.add('popup-bubble', 'popup-bubble--field')

            let containerDiv = document.createElement('div')
            containerDiv.classList.add('popup-container')
            containerDiv.appendChild(content)

            let LabelMarker = new this.google.maps.marker.AdvancedMarkerElement({
                position: coords,
                content: containerDiv,
            })

            LabelMarker.addListener('spider_click', () => {
                this.centerToField(f)
            })
            this.markerSpidifier.addMarker(LabelMarker)

            return LabelMarker
        },
        drawField(f, drawLabel = true) {
            const poly = new this.google.maps.Polygon({
                paths: f.outer_border.coordinates.map((items) => items.map((item) => ({ lat: item[1], lng: item[0] }))),
                strokeColor: f.color,
                strokeOpacity: 1,
                strokeWeight: 3,
                fillColor: f.color,
                fillOpacity: 0.5,
                editable: false,
                visible: false,
                clickable: true
            })
            let fb = { ...f, poly }
            fb.poly.setMap(this.map)
            this.google.maps.event.addListener(fb.poly, 'click', () => this.centerToField(fb))
            if (drawLabel) {
                fb.popup = this.drawLabel(fb)
            }
            return fb
        },

        drawFields() {
            this.fields.forEach((f, i) => {
                if (this.fields[i].poly) {
                    this.showField(f.id, false)
                } else {
                    // drawField has visibility set to false
                    this.fields[i] = this.drawField(f)
                    // Make newly created field visible
                    this.fields[i].poly.setVisible(true)
                }
            })

        },
        // this function is used now only in FieldsManagementView
        async drawSVGFields() {
            const fieldsTable = useFieldsManagementStore()
            await fieldsTable.fields.forEach((f) => {
                this.drawGeoSVG(f)
                f.table_svg_id = `table-svg-${f.id}`
            })

        },
        async drawMarkerSpidifier() {
            const OverlappingMarkerSpiderfier = window.OverlappingMarkerSpiderfier

            this.markerSpidifier = new OverlappingMarkerSpiderfier(toRaw(this.map), {
                markersWontMove: true,
                markersWontHide: true,
                basicFormatEvents: false,
                keepSpiderfied: false,
                nearbyDistance: 20,
                circleSpiralSwitchover: 100,
                circleFootSeparation: 50,
                spiralFootSeparation: 50,
                spiralLengthStart: 100,
                spiralLengthFactor: 100,
            })

            this.markerSpidifier.addListener('format', (marker, status) => {
                let content = marker.content.querySelector('.popup-bubble')
                if (status === 'SPIDERFIABLE') {
                    content.classList.add('warning')
                } else {
                    content.classList.remove('warning')
                }
            })
        },
        async drawMarkerClusterer() {
            this.markerCluster = new MarkerClusterer({
                map: toRaw(this.map),
                markers: this.fields.map((f) => f.popup),
                algorithmOptions: { maxZoom: FIELD_LABEL_MIN_ZOOM },
            })
        },
        disableFieldLabels() {
            if (this.markerSpidifier) {
                this.markerSpidifier.clearMarkers()
                // this.markerSpidifier = null
            }
            if (this.markerCluster) {
                this.markerCluster.clearMarkers()
                this.markerCluster = null
            }
        },
        async enableFieldLabels() {
            // Check if google and map are initialized
            if (!this.google || !this.map) {
                return
            }
            // Clear first, otherwise the same markers end up in the clusterer twice.
            this.disableFieldLabels()
            // Draw marker spiderifier if it doesn't exist
            if (!this.markerSpidifier) {
                await this.drawMarkerSpidifier()
            } else {
                // Re-add markers to the existing spiderifier
                this.fields.forEach(field => {
                    if (field.popup) {
                        this.markerSpidifier.addMarker(field.popup)
                    }
                })
            }
            await this.drawMarkerClusterer()
        },
        async getAllFieldsDetails(fields) {
            // reset the displayed list and counter
            this.fields = []
            this.fieldsLoaded = 0
            this.fieldsErrored = 0

            const list = Array.isArray(fields) ? fields : Object.values(fields)
            this.fieldsTotal = list.length

            for (let i = 0; i < list.length; i++) {
                const f = list[i]

                const coords = f.outer_border?.coordinates?.[0]

                // if we don't have valid coords, log and skip
                if (!coords || !Array.isArray(coords)) {
                    console.warn('Field without valid outer_border', f.id, f.outer_border)
                    this.fieldsErrored++
                    continue
                }

                // prepare data for map and list
                this.extendMapCenter(coords)
                f.bounds = this.genFieldBounds(coords)
                f.color = colors[i % colors.length]

                // add immediately to the displayed list
                this.fields.push(f)
                this.fieldsLoaded = this.fields.length

                // let the UI breathe after every N fields
                if (i % 25 === 0) {
                    await new Promise((resolve) => setTimeout(resolve, 0))
                }
            }
        },
        async getAllFields(centerMap) {
            this.loading.fieldList = true
            const cStore = useCustomers()
            let fObj
            if (cStore.selectedCustomers.length === 0) {
                fObj = await taskAPI.getFields({
                    organization_ids: cStore.customers.map((c) => c.id),
                    expand_data: ['outer_border']
                })
            } else {
                fObj = await taskAPI.getFields({
                    organization_ids: cStore.selectedCustomers.map((c) => c.id),
                    expand_data: ['outer_border']
                })
            }

            // Clear existing fields and related elements
            this.clearMap()

            this.mapCenter = new this.google.maps.LatLngBounds()

            // start incremental processing of fields
            await this.getAllFieldsDetails(fObj)

            await this.drawMarkerSpidifier()

            this.drawFields()

            await this.drawMarkerClusterer()

            if (centerMap && this.fields.length > 0) {
                this.centerMap()
            }

            // now consider the map loading as done
            this.loading.fieldList = false
        },
        centerMap() {
            // Empty bounds → Google Maps centers near (0, 180); keep previous geo instead
            if (!this.mapCenter || this.mapCenter.isEmpty()) {
                return
            }

            if (this.mapCenter.getCenter()) {
                this.map.fitBounds(this.mapCenter)
                this.filteredFields = this.fields
                this.filteredFields.forEach((f) => {
                    f.poly.setVisible(true)
                })
                // update geo coordinates
                this.geo.lat = this.map.getCenter().lat()
                this.geo.long = this.map.getCenter().lng()
                this.geo.zoom = this.map.getZoom()
            }
        },
        async getAllDisabledFields() {
            this.disabledFields = []
            const cStore = useCustomers()

            try {
                let fObj
                if (cStore.selectedCustomers.length === 0) {
                    fObj = await taskAPI.getDisabledFields(cStore.customers.map((c) => c.id))
                } else {
                    fObj = await taskAPI.getDisabledFields(cStore.selectedCustomers.map((c) => c.id))
                }

                // Populate disabledFields with the fetched data
                this.disabledFields = fObj.map(field => {
                    field.poly = { visible: false }
                    return field
                })

            } catch (error) {
                console.error('There was a problem fetching disabled fields:', error)
            }
        },
        async getField(fid) {
            try {
                // Get the field data from the API
                const fieldData = await taskAPI.getField(fid)

                if (!fieldData) {
                    console.error('Could not get field data:', fid)
                    await this.handleFieldNotFound()
                    return null
                }

                // If the field exists in the list of fields, get the visual information
                const existingFieldIdx = this.fields.findIndex(f => f.id === parseInt(fid))

                if (existingFieldIdx !== -1) {
                    // Transfer the visual attributes to the new field object
                    fieldData.poly = this.fields[existingFieldIdx].poly
                    fieldData.popup = this.fields[existingFieldIdx].popup
                    fieldData.bounds = this.fields[existingFieldIdx].bounds
                    fieldData.color = this.fields[existingFieldIdx].color
                } else {
                    // If the field does not exist in the current list, generate visual attributes
                    if (fieldData.outer_border && fieldData.outer_border.coordinates) {
                        fieldData.bounds = this.genFieldBounds(fieldData.outer_border.coordinates[0])
                        const idx = this.fields.length % colors.length
                        fieldData.color = colors[idx]

                        // Create the polygon for the map (will be assigned later to the map)
                        fieldData.poly = new this.google.maps.Polygon({
                            paths: fieldData.outer_border.coordinates[0].map(coord => ({
                                lat: coord[1],
                                lng: coord[0]
                            })),
                            strokeColor: fieldData.color,
                            strokeOpacity: 1.0,
                            strokeWeight: 2,
                            fillColor: fieldData.color,
                            fillOpacity: 0.35,
                            visible: false
                        });

                        this.drawField(fieldData, false)
                    } else {
                        console.error('Field does not have a valid outer_border:', fid)
                        return null
                    }
                }

                return fieldData;
            } catch (error) {
                console.error('Error in getField:', error)
                return null
            }
        },
        async disableField(field) {
            if (!field?.id) {
                console.error('No field provided for disable')
                return false
            }

            this.loading.fieldDisable = true
            try {
                const success = await taskAPI.disableFieldAPI(field.id)
                if (!success) {
                    throw new Error('Failed to delete field!')
                }

                await this.getAllFields(false)

                const tStore = useTasksStore()
                await this.router.push(this.buildPath('fields', field.farm_id))
                tStore.reset()
                return true
            } catch (error) {
                console.error('There was a problem with the fetch operation:', error)
                return false
            } finally {
                this.loading.fieldDisable = false
            }
        },
        async restoreField(fieldId) {
            const data = {
                id: fieldId,
                active: true
            }

            this.loading.fieldRestore = true
            try {
                const response = await taskAPI.updateField(data)
                if (!response) {
                    throw new Error('Failed to enable field!')
                }

                await this.getAllFields(false)
                await this.navToField(response.id, '/details')
                return true
            } catch (error) {
                console.error('There was a problem with the fetch operation:', error)
                return false
            } finally {
                this.loading.fieldRestore = false
            }
        },
        drawHeadlandSegments(field) {
            if (!field.headland_segments || !field.headland_segments.geometries) {
                console.warn('No headland segments found for field:', field.id)
                return
            }

            // That prevents the segments drawing twice
            if (this.draw.fields.length === 0 && this.draw.field.segments.length > 0) {
                return
            }

            // Shuffle the colors at the first display (when there are no segments already drawn)
            if (this.draw.field.segments.length === 0) {
                this.randomRainbowColors = [...this.randomRainbowColors].sort(() => Math.random() - 0.5)
            }

            try {
                // some segments are invalid, use try/catch to avoid app crashing
                let colorIndex = 0
                for (const segmentCollection of field.headland_segments.geometries) {
                    for (const segment of segmentCollection.geometries) {
                        for (const line of segment.coordinates) {
                            const polyline = new this.google.maps.Polyline({
                                path: line.map((item) => ({ lat: item[1], lng: item[0] })),
                                strokeColor: this.randomRainbowColors[colorIndex % this.randomRainbowColors.length],
                                strokeOpacity: 1,
                                strokeWeight: 2,
                                fillColor: this.randomRainbowColors[colorIndex % this.randomRainbowColors.length],
                                fillOpacity: 1,
                                geodesic: true, // The line will follow the curvature of the Earth
                                editable: false,
                                zIndex: 3
                            });
                            polyline.setMap(toRaw(this.map))
                            this.draw.field.segments.push(polyline)
                            colorIndex++
                        }
                    }
                }
            } catch (e) {
                console.warn('Invalid headland segments geometry for field:', field.id)
            }
        },
        clearHeadlandSegments() {
            this.draw.field.segments.forEach((segment) => {
                toRaw(segment).setMap(null)
            })
            this.draw.field.segments = []
        },
        shuffleSegmentColors() {
            const ts = useTasksStore()
            
            // Shuffle the array of colors
            this.randomRainbowColors = [...this.randomRainbowColors].sort(() => Math.random() - 0.5)
            
            // If we have segments already displayed on the map, apply the colors directly
            if (this.draw.field.segments && this.draw.field.segments.length > 0) {
                this.draw.field.segments.forEach((segment, index) => {
                    const polyline = toRaw(segment)
                    polyline.setOptions({
                        strokeColor: this.randomRainbowColors[index % this.randomRainbowColors.length]
                    })
                })
                return
            }
            
            // Fallback: redraw the segments if we have data in taskStore
            if (ts.field && ts.field.headland_segments) {
                this.clearHeadlandSegments()
                this.drawHeadlandSegments(ts.field)
            }
        },
        genPath(gjson, w, h) {
            let width = w
            let height = h
            let p = d3.geoMercator().scale(1).translate([0, 0])
            let path = d3.geoPath().projection(p)
            let b = path.bounds(gjson),
                s = 0.95 / Math.max((b[1][0] - b[0][0]) / width, (b[1][1] - b[0][1]) / height),
                t = [(width - s * (b[1][0] + b[0][0])) / 2, (height - s * (b[1][1] + b[0][1])) / 2]
            p.scale(s).translate(t)
            return path
        },
        update(geojson, f, fill = true) {
            /**
             * Prefer table SVG (#table-svg-<id>) when present;
             * fallback to list SVG (#svg-<id>)
             * fill: if false, no fill (e.g. LineString/Point)
             */
            let svg = d3.select('#table-svg-' + f.id)
            if (!svg.node()) {
                svg = d3.select('#svg-' + f.id)
            }

            // Check if SVG element exists
            if (!svg.node()) {
                return
            }

            let w = svg.node().getBoundingClientRect().width
            let h = svg.node().getBoundingClientRect().height

            svg.selectChildren().remove()
            svg.append('g')
                .selectAll('path')
                .data(geojson.features)
                .enter()
                .append('path')
                .attr('d', this.genPath(geojson, w, h))
                .attr('fill', (feature) => {
                    if (feature.properties?.fill === 'none') {
                        return 'none'
                    }
                    return fill ? (feature.properties?.fill ?? f.color) : 'none'
                })
                .attr('fill-opacity', (feature) => (
                    feature.properties?.fill === 'none' ? 0 : (fill ? '0.2' : 0)
                ))
                .attr('stroke', (feature) => feature.properties?.stroke ?? f.color)
                .attr('stroke-width', (feature) => feature.properties?.strokeWidth ?? 2)
                .attr('stroke-opacity', 1)
        },
        async drawGeoSVG(f) {
            let geojson = transformBordersToGeoJSON(f)
            geojson.features = geojson.features.map((f) => {
                return turf.rewind(f, { reverse: true })
            })

            let area = turf.area(geojson)
            f.area = area

            this.update(geojson, f)
        },
        async drawSVG(f) {
            var x = d3
                .scaleLinear()
                .range([0, 50])
                .domain(d3.extent(f.outer_border.coordinates[0], (d) => d[0]))

            var y = d3
                .scaleLinear()
                .range([75, 0])
                .domain(d3.extent(f.outer_border.coordinates[0], (d) => d[1]))

            var line = d3
                .line()
                .x(function (d) {
                    return x(d[0])
                })
                .y(function (d) {
                    return y(d[1])
                })
            d3.select('#svg-' + f.id)
                .selectChildren()
                .remove()
            d3.select('#svg-' + f.id)
                .attr('width', 50)
                .attr('height', 75)
                .attr('fill', f.color)
                .attr('fill-opacity', '0.5')
                .attr('stroke', f.color)
                .attr('stroke-width', 2)
                .attr('stroke-opacity', 1)
                .append('path')
                .datum(f.outer_border.coordinates[0])
                .attr('d', line)
        },

        // drawInnerBorder(f) {},

        drawABLines(f) {
            if (this.draw.field.abLines.length > 0 && this.draw.fields.length === 0) {
                return
            }

            // Reset the flag on each drawABLines call
            this.draw.field.hasSpecialABLines = false

            const fls = useFieldLayersStore()
            const abLinesColor = fls.getLayer('ab_lines').color || '#00FF00'
            for (const hl of f.ab_lines.coordinates) {
                const wideLine = new this.google.maps.Polyline({
                    path: hl.map((item) => ({ lat: item[1], lng: item[0] })),
                    strokeColor: abLinesColor,
                    strokeOpacity: 1,
                    strokeWeight: 3,
                    fillColor: abLinesColor,
                    fillOpacity: 1,
                    geodesic: true, // The line it will follow the curvature of the Earth
                    editable: false,
                    zIndex: 1,
                    clickable: false,
                })

                this.checkIfHasSpecialABLines(wideLine)

                wideLine.setMap(toRaw(this.map))
                this.draw.field.abLines.push(wideLine)
            }
        },
        drawTrackWheels(f) {
            if (!f.ab_track_lines || !f.ab_track_lines.coordinates) {
                console.warn('No track wheels found for field:', f.id)
                return
            }

            const fls = useFieldLayersStore()
            const trackWheelsColor = fls.getLayer('track_wheels')?.color || '#00FFFF'
            
            for (const tw of f.ab_track_lines.coordinates) {
                const trackWheel = new this.google.maps.Polyline({
                    path: tw.map((item) => ({ lat: item[1], lng: item[0] })),
                    fillOpacity: 0,
                    strokeColor: trackWheelsColor,
                    strokeWeight: 2,
                    strokeOpacity: 0,
                    icons: [{
                        icon: {
                            path: 'M 0,0 0,1',
                            strokeOpacity: 0, // Hidden by default
                            scale: 4
                        },
                        offset: '0',
                        repeat: '20px'
                    }],
                    zIndex: 100,
                    clickable: false,
                })
                trackWheel.setMap(toRaw(this.map))
                this.draw.field.trackWheels.push(trackWheel)
            }
        },
        checkIfHasSpecialABLines(wideLine) {
            const points = wideLine.getPath().getArray().length
            // Normal AB Lines has only 2 points, if the line has more than 2 points, it has special AB lines
            if (points > 2) {
                this.draw.field.hasSpecialABLines = true
            }
        },
        drawHeadLands(f) {
            if (this.draw.field.headlanes.length > 0 && this.draw.fields.length === 0) {
                return
            }
        
            // Reset the array for the field headlanes
            this.draw.field.fieldHeadlanes = []

            const fls = useFieldLayersStore()
            const headlandColor = fls.getLayer('headland').color || '#40E0D0'
            for (let geometryIndex = 0; geometryIndex < f.headland.geometries.length; geometryIndex++) {
                const hl = f.headland.geometries[geometryIndex]
                let isFirstRingOfGeometry = true
                
                for (const polygon of hl.coordinates) {
                    for (const ring of polygon) {
                        const poly = new this.google.maps.Polyline({
                            path: ring.map((item) => ({ lat: item[1], lng: item[0] })),
                            strokeColor: headlandColor,
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            fillColor: headlandColor,
                            fillOpacity: 1,
                            geodesic: true, // The line it will follow the curvature of the Earth
                            editable: false,
                            clickable: false,
                            zIndex: 1
                        })
                        poly.setMap(toRaw(this.map))
                        this.draw.field.headlanes.push(poly)
                        
                        // Save the first ring from each geometry ONLY for the field headlanes
                        if (isFirstRingOfGeometry) {
                            this.draw.field.fieldHeadlanes.push(poly)
                        }
                        isFirstRingOfGeometry = false
                    }
                }
            }
        },
        startPulsingHeadland(headlandIndex) {
            this.stopPulsingHeadland()

            const polyline = this.draw.field.fieldHeadlanes[headlandIndex - 1]
            if (!polyline) return
            
            let isGrowing = true
            let weight = 2
            
            const wps = useWarningPlanner()
            this.pulseHeadlandInterval = setInterval(() => {
                weight += isGrowing ? 2 : -2
                if (weight >= 8) isGrowing = false
                if (weight <= 2) isGrowing = true
                
                let strokeColor = '#00FF00' // default green for Safe
                if (wps.warning && wps.warning.level === 'Critical') {
                    strokeColor = '#FF0000' // red for Critical
                } else if (wps.warning && wps.warning.level === 'High') {
                    strokeColor = '#FF8800' // orange for High
                } else if (wps.warning && wps.warning.level === 'Safe') {
                    strokeColor = '#00FF00' // green for Safe
                }
                
                polyline.setOptions({ 
                    strokeWeight: weight, 
                    zIndex: 5, // it will be over the field segments, but under the generated path route
                    strokeColor: strokeColor
                })
            }, 100)
        },
        stopPulsingHeadland() {
            if (this.pulseHeadlandInterval) {
                clearInterval(this.pulseHeadlandInterval)
                this.pulseHeadlandInterval = null
                
                if (this.draw.field.fieldHeadlanes) {
                    this.draw.field.fieldHeadlanes.forEach(polyline => {
                        toRaw(polyline).setOptions({ 
                            strokeWeight: 2, 
                            zIndex: 1, 
                            strokeColor: '#000000' 
                        })
                    })
                }
            }
        },
        hideNavField() {
            this.draw.field.abLinesMarkers.map((f) => {
                toRaw(f).setMap(null)
            })
            this.draw.field.abLines.map((f) => {
                toRaw(f).setMap(null)
            })
            this.draw.field.headlanes.map((f) => {
                toRaw(f).setMap(null)
            })
            this.draw.field.headlanesMarkers.map((f) => {
                toRaw(f).setMap(null)
            })
            this.draw.field.fieldHeadlanes.map((f) => {
                toRaw(f).setMap(null)
            })
            this.draw.field.trackWheels.map((f) => {
                toRaw(f).setMap(null)
            })

            this.draw.field.abLines = []
            this.draw.field.abLinesMarkers = []
            this.draw.field.headlanes = []
            this.draw.field.headlanesMarkers = []
            this.draw.field.fieldHeadlanes = []
            this.draw.field.trackWheels = []

            this.clearHeadlandSegments()

            const fieldsConnectionStore = useFieldsConnectionStore()
            fieldsConnectionStore.clearFieldROIs()

            this.hideFarmROILayer()
            this.hideParkingLotsLayer()
        },
        hideField(fieldId) {
            let index = this.fields.findIndex((f) => f.id === fieldId)
            let fieldPoly = toRaw(this.fields[index].poly)
            fieldPoly.setMap(null)
            this.fields[index].poly.setVisible(false)
            // let content = this.fields[index].labelContent
            // content.classList.add('inactive')
        },
        showField(fieldId, withLabel = true) {
            let index = this.fields.findIndex((f) => f.id === fieldId)
            this.fields[index].poly.setVisible(true)
            this.fields[index].poly.setMap(toRaw(this.map))
            toRaw(this.fields[index].poly).setOptions({ fillOpacity: 0.5, clickable: true })
        },
        async showAllBorders(fieldId = null) {
            await this.enableFieldLabels()
            this.fields.map((f) => {
                if (fieldId == null || fieldId === '' || f.id !== fieldId) {
                    f.poly.setVisible(true)
                    toRaw(f.poly).setMap(toRaw(this.map))
                    toRaw(f.poly).setOptions({ fillOpacity: 0.5, clickable: true })
                }
            })

            const fieldsConnectionStore = useFieldsConnectionStore()
            fieldsConnectionStore.clearFieldROIs()
        },
        hideFieldFills() {
            for (const field of this.fields) {
                if (!field.poly || field.fillHidden) {
                    continue
                }

                field.fillHidden = true
                toRaw(field.poly).setOptions({ fillOpacity: 0, clickable: false })

                if (field.popup) {
                    toRaw(field.popup).gmpClickable = false
                }
            }
        },
        showFieldFills() {
            for (const field of this.fields) {
                if (!field.poly || !field.fillHidden) {
                    continue
                }

                field.fillHidden = false
                toRaw(field.poly).setOptions({ fillOpacity: 0.5, clickable: true })

                if (field.popup) {
                    toRaw(field.popup).gmpClickable = true
                }
            }
        },
        hideAllBorders(fieldId = null, firstRender = false) {
            this.disableFieldLabels()
            this.fields.map((f) => {
                // First render comes from Drawing.vue and we need to setup the edited field
                if (fieldId === f.id && firstRender) {
                    toRaw(f.poly).setOptions({ fillOpacity: 0, clickable: false })
                    toRaw(f.popup).setMap(null)
                    toRaw(f.popup).visible = false
                }
                // if we have fieldId, we hide all borders except the one with the id
                if (fieldId == null || fieldId === '' || f.id !== fieldId) {
                    if (f.poly) {
                        f.poly.visible = false
                        toRaw(f.poly).setMap(null)
                    }
                    if (f.popup) {
                        toRaw(f.popup).setMap(null)
                        toRaw(f.popup).visible = false
                    }
                }
            })
        },
        async drawFieldGeoSVG() {
            const tStore = useTasksStore()
            if (tStore.field) {
                await this.drawGeoSVG(tStore.field, tStore.field.color)
            }
        },
        async renderField() {
            const tStore = useTasksStore()

            if (!tStore.field) {
                return
            }

            this.hideAllBorders()
            this.showField(tStore.field.id, false)
            this.drawHeadLands(tStore.field)
            this.drawABLines(tStore.field)
            this.drawHeadlandSegments(tStore.field)

            // draw track wheels to keep track wheels stored in the field
            this.drawTrackWheels(tStore.field)

            // hide track wheels layer by default
            this.hideTrackWheelsLayer()

            // hide farm ROI layer by default
            this.hideFarmROILayer()

            // hide parking lots layer by default
            this.hideParkingLotsLayer()

            this.disableFieldLabels()

            const fieldsConnectionStore = useFieldsConnectionStore()
            fieldsConnectionStore.clearFieldROIs()
            await fieldsConnectionStore.getFieldROIs(tStore.field.id)
        },
        hideTrackWheelsLayer() {
            const fls = useFieldLayersStore()

            const trackWheelsLayer = fls.layers.find(layer => layer.id === 'track_wheels')
            if (trackWheelsLayer) {
                trackWheelsLayer.visible = false
                fls.toggleLayerVisibility(trackWheelsLayer)
            }
        },
        hideFarmROILayer() {
            const fls = useFieldLayersStore()

            const farmROILayer = fls.layers.find(layer => layer.id === 'farm_roi')
            if (farmROILayer) {
                farmROILayer.visible = false
                fls.toggleLayerVisibility(farmROILayer)
            }
            const farmROINew = fls.newLayers?.find(layer => layer.id === 'farm_roi')
            if (farmROINew) {
                farmROINew.visible = false
                fls.toggleNewLayerVisibility(farmROINew)
            }
        },
        hideParkingLotsLayer() {
            const fls = useFieldLayersStore()

            const parkingLotsLayer = fls.layers.find(layer => layer.id === 'parking_lots')
            if (parkingLotsLayer) {
                parkingLotsLayer.visible = false
                fls.toggleLayerVisibility(parkingLotsLayer)
            }
            const parkingLotsNew = fls.newLayers?.find(layer => layer.id === 'parking_lots')
            if (parkingLotsNew) {
                parkingLotsNew.visible = false
                fls.toggleNewLayerVisibility(parkingLotsNew)
            }
        },
        async animateToField(field) {
            // Step 1: Find the field bounds
            let bounds

            // Try different ways to get field bounds
            if (field.poly && typeof field.poly.poly_getBounds === 'function') {
                bounds = field.poly.poly_getBounds()
            } else if (field.bounds) {
                bounds = field.bounds;
            } else if (
                field.outer_border &&
                field.outer_border.coordinates &&
                field.outer_border.coordinates[0]?.length > 0
            ) {
                bounds = this.genFieldBounds(field.outer_border.coordinates[0])
            } else {
                console.warn('No bounds found for field animation')
                return
            }

            /**
             * TODO: This is a hack to get the map to animate to the field
             * We should find a better way to do this
             */
            return new Promise((resolve) => {
                // Get and adjust zoom within valid range
                let actualZoom = this.map.getZoom()
                if (actualZoom > 1 && actualZoom < 22) {
                    actualZoom--
                } else {
                    actualZoom++
                }

                // Change zoom a little to trigger map idle
                this.map.setZoom(actualZoom)
                this.google.maps.event.addListenerOnce(this.map, 'idle', () => {
                    this.map.fitBounds(bounds, {padding: 50})
                    resolve()
                })
            })
        },
        async navToField(fid, route) {
            const tStore = useTasksStore()
            const cs = useCustomers()

            // wait for the map to be loaded
            while (this.loading.map) {
                await new Promise(resolve => setTimeout(resolve, 100))
            }
            try {
                // Step 1: Get field details
                const field = await this.getField(fid)
                if (!field) {
                    await this.handleFieldNotFound()
                    return false
                }

                // Step 2: Set up the task store with the field
                tStore.reset()
                tStore.field = field
                const fieldCustomer = cs.customers.find(c => c.id === field.farm_id)
                if (fieldCustomer) {
                    this.setCurrentCustomer(fieldCustomer)
                } else {
                    cs.selectedCustomers = []
                }

                // Step 3: if route includes /task or /tasks, load field tasks
                const isTaskRoute = route.includes('/task')
                if (isTaskRoute) {
                    await tStore.getFieldTasksByPeriod(field.id, tStore.activeTasksPeriod)
                }

                await this.renderField()

                // Step 4: Animate to the field with smooth animation
                await this.animateToField(field)

                // Step 5: Navigate to the specified route
                if (route) {
                    await this.router.push('/field/' + field.id + route)
                } else {
                    await this.router.push('/field/' + field.id + '/details')
                }

                return true
            } catch (error) {
                console.error('Error navigating to field:', error)
                return false
            } finally {
                tStore.loading = false
            }
        },
        setCurrentCustomer(fieldCustomer) {
            const cs = useCustomers()

            // set the selected customer to the field customer
            cs.selectedCustomers = [fieldCustomer]
            // setMap(null) the fields that are not for the selected customer
            this.fields.forEach(f => {
                if (f.farm_id !== fieldCustomer.id) {
                    toRaw(f.poly).setMap(null)
                    toRaw(f.popup).setMap(null)
                }
            })
            // the new fields array will only contain the fields for the selected customer
            this.fields = this.fields.filter(f => f.farm_id === fieldCustomer.id)
        },
        async navToTask(task, options = {}) {
            const tStore = useTasksStore()
            this.loading.fieldTasks = true

            try {
                // wait for the map to be loaded
                while (this.loading.map) {
                    await new Promise(resolve => setTimeout(resolve, 100))
                }
                this.hideNavField()

                // Step 1: Get field details
                const field = await this.getField(task.field_id)
                if (!field) {
                    this.handleFieldNotFound()
                    throw new Error('Field not found')
                }

                // Step 2: Set up the task store with the field
                tStore.reset()
                tStore.field = field
                tStore.pendingScrollTaskId = task.id
                tStore.pendingScrollCheck = !!options.checkAsApplied
                tStore.pendingScrollNexatMachineId = options.nexatMachineId ?? null

                // Step 3: Load tasks for the period used by TaskList
                const period = options.ensurePeriod ?? 'all'
                tStore.activeTasksPeriod = period
                await tStore.getFieldTasksByPeriod(field.id, period)

                await this.renderField()

                // Step 4: Animate to the field with smooth animation
                await this.animateToField(field)

                // Step 5: Always open TaskList (TaskDetails removed)
                // Push while fieldTasks is still true so FieldLayout mounts the centered skeleton
                await this.router.push('/field/' + field.id + '/tasks')
            } catch (error) {
                console.error('Error navigating to task:', error)
            } finally {
                this.loading.fieldTasks = false
                this.loading.fieldList = false
                tStore.loading = false
            }
        },
        async handleFieldNotFound() {
            const locationNow = this.router.currentRoute.value.path
            // if the location is already /fields/, do not navigate to /fields/ (exemple from fields list view)
            if (locationNow.startsWith('/fields')) {
                return
            }
            const cs = useCustomers()
            await this.router.push(this.buildPath('fields', cs.selectedCustomers[0]?.id))
        },
        centerToField(field) {
            this.map.fitBounds(field.bounds)
            const f = document.getElementById('field-' + field.id)
            if (f) {
                f.scrollIntoView({ behavior: 'smooth', block: 'center' })
                this.selected = field.id

                // Show the field list on small screens when clicking on a field
                const appState = useAppState()
                appState.showFieldList()
            }
        },
        clearMap() {
            // Clear any existing polygons
            this.fields.forEach(field => {
                if (field.poly) {
                    toRaw(field.poly).setMap(null)
                }
                if (field.popup) {
                    toRaw(field.popup).setMap(null)
                }
            })

            this.hideNavField()

            // Reset fields array
            this.fields = []
            this.fieldsLoaded = 0
            this.fieldsTotal = 0
            this.fieldsErrored = 0

            // Clear any spidifier markers
            if (this.markerSpidifier) {
                this.markerSpidifier.clearMarkers()
                this.markerSpidifier = null
            }

            // Clear any cluster markers
            if (this.markerCluster) {
                this.markerCluster.clearMarkers()
                this.markerCluster = null
            }
        },
        async downloadFieldData(fieldId, fieldName) {
            let res = await taskAPI.downloadFieldDataZip(fieldId)
            let now = moment().format('YYYY-MM-DD-HH-mm')
            if (res) {
                saveAs(res, `field_${fieldId}_data_${fieldName}_${now}.zip`)
            }
            return res
        },
        async downloadFieldBorder(fieldId, fieldName) {
            let res = await taskAPI.downloadFieldBorderZip(fieldId)
            let now = moment().format('YYYY-MM-DD-HH-mm')
            if (res) {
                saveAs(res, `field_${fieldId}_border_${fieldName}_${now}.zip`)
            }
            return res
        },
        async downloadOnlyCutouts(field, fieldName) {
            if (!field?.outer_border?.coordinates?.length) {
                return false
            }
            // Polygon: ring[0] = border; ring[1..n] = holes
            const cutouts = field.outer_border.coordinates.slice(1)
            if (cutouts.length === 0) {
                return false
            }
            const fileName = `field_${field.id}_only_cutouts_${fieldName}_${moment().format('YYYY-MM-DD-HH-mm')}`
            const cutoutsFeature = {
                type: 'Feature',
                geometry: {
                    type: 'MultiPolygon',
                    coordinates: cutouts.map((ring) => [ring]),
                },
                properties: {
                    field_id: field.id,
                    type: 'cutouts',
                },
            }
            try {
                await downloadFeatureAsShapefile(cutoutsFeature, fileName)
                return true
            } catch (e) {
                console.error('downloadOnlyCutouts failed', e)
                return false
            }
        },
        async getFieldWithExpansions(fieldId, expansions = [], fieldName) {
            let res = await taskAPI.getFieldWithExpansions(fieldId, expansions)
            let now = moment().format('YYYY-MM-DD-HH-mm')
            const containExp = getInitialsCode(expansions)
            if (res) {
                saveAs(res, `field_${fieldId}_${containExp}_${fieldName}_${now}.zip`)
            }
            return res
        },
        applyFieldClickable(clickable, f_id) {
            const field = this.fields.find(f => f.id === f_id)
            if (!field) return
            if (field.poly) {
                toRaw(field.poly).setOptions({ clickable })
                toRaw(field.poly).gmpClickable = clickable
            }
            if (field.popup) {
                toRaw(field.popup).gmpClickable = clickable
            }
        },

    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useMaps, import.meta.hot))
}
